import type { Schema } from '@dealdesk/api-schema';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FileActions, StatusBadge } from '@/business';
import { Button, UploadIcon } from '@/design-system';
import { useCan } from '@/shared/auth';

import { fetchDealDocumentLink } from '../api/queries';
import { canReplace, fileRefused } from '../model/documents';
import { DocumentUploadDialog } from './DocumentUploadDialog';

type Doc = Schema<'DealDocument'>;

/**
 * The documents a bank update or an operational step asked for, with their state and an inline upload — the user
 * answers the request without leaving the dialog or the step. Review stays on the Documents tab.
 */
export function LinkedRequirements({ dealId, documents, title, closed }: { dealId: string; documents: readonly Doc[]; title: string; closed?: boolean }) {
  const { t } = useTranslation('deals');
  const can = useCan();
  const [uploading, setUploading] = useState<Doc | null>(null);
  if (!documents.length) return null;
  const canUpload = can('document.upload') && !closed;
  return (
    <section className="flex flex-col" aria-label={title}>
      <h4 className="pb-1 text-label text-ink-secondary">{title}</h4>
      <ul className="flex flex-col border-y border-border">
        {documents.map((d) => (
          <li key={d.id} className="flex min-w-0 items-center gap-3 border-b border-border py-1.5 last:border-b-0">
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-body text-ink">{d.name}</span>
              {d.fileName ? (
                <span className="truncate text-meta text-ink-muted" dir="auto">
                  {d.fileName}
                </span>
              ) : null}
            </span>
            {fileRefused(d) && d.scanStatus ? <StatusBadge registry="fileScanStatus" value={d.scanStatus} /> : <StatusBadge registry="documentStatus" value={d.status} />}
            {d.evidence && d.fileName && !fileRefused(d) ? <FileActions fileName={d.fileName} scanStatus={d.scanStatus ?? null} fetchLink={(disposition) => fetchDealDocumentLink(dealId, d.id, disposition)} /> : null}
            {canUpload && canReplace(d) ? (
              <Button size="xs" variant={d.evidence ? 'ghost' : 'secondary'} icon={<UploadIcon size={13} />} aria-label={t('documents.fileLabel', { name: d.name })} onClick={() => setUploading(d)}>
                {d.evidence ? t('documents.replace') : t('documents.upload')}
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
      {uploading ? <DocumentUploadDialog dealId={dealId} doc={uploading} onClose={() => setUploading(null)} /> : null}
    </section>
  );
}
